// EXTERNAL DEPENDENCIES
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

// HELPERS
import { useImpressionTracking } from '@/hooks'
import {
  useBusinessProfilePageContent,
  useBusinessProfileReduxState,
  useErrorResetting,
  useModularSellerViewPageContent,
} from '../../hooks'
import {
  updateAreValuesEdited,
  updateEditedProfileDataValues,
} from '@/redux/features'

// COMPONENTS
import { Card, Link, Row } from '@paypalcorp/pp-react'
import ContactInfoSection from '@/common/components/ui/contact-info-section'
import ExitConfirmationBox from '@/common/components/ui/confirmation-box'
import {
  BusinessNameSection,
  EditBusinessLocationNameSection,
  PageHeaderSection,
  PreviewProfileButton,
  SaveEditsButton,
} from '../'

// CONSTANTS
import { commonModularProfileFptiProperties } from '@/constants'
import { routes } from '@/api/constants'
import { EDIT_BIZ_INFO_PAGE_COMPONENT_TEST_ID } from '../../constants/test-ids'

// FPTI
import {
  EDIT_BIZ_INFO_BACK_LINK_CLICK_FPTI,
  EDIT_BIZ_INFO_PAGE_SHOWN_FPTI,
  EDIT_BIZ_INFO_PREVIEW_BUTTON_CLICK_FPTI,
} from './fpti'
import { MODULAR_EDIT_PROFILE_PANEL_EDIT_BUSINESS_INFO_FPTI } from '../../fpti'
import {
  EDIT_BIZ_INFO_SAVE_BUTTON_CLICK_FPTI,
  EDIT_BIZ_INFO_SUCCESSFUL_SAVE_FPTI,
} from '../owner-profile-page/constants/fpti'

// STYLES
import styles from './styles.module.less'

// TYPES
import { FormattedEditedProfileData } from '../../types/state'
import { LocationDisplayPreference } from '@/types'

/**
 * Allows the profile owner to edit their business info, such as
 * their address display preference and displayed contact info
 */
export function EditBizInfoPage({
  goBackToMainPage,
}: {
  goBackToMainPage: () => void
}) {
  useImpressionTracking(EDIT_BIZ_INFO_PAGE_SHOWN_FPTI)

  // DECLARATIONS
  const isEditedDataInitialized = useRef(false)
  const dispatch = useDispatch()

  // INITIALIZE CONTENT
  const getContent = useBusinessProfilePageContent()
  const getModularSellerViewPageContent = useModularSellerViewPageContent()
  const bizInfoUpdatedToastMessage = getModularSellerViewPageContent(
    'bizInfoUpdatedToast',
  )

  // STATE
  const {
    uneditedFormattedData: {
      allContactInfo,
      businessName,
      contactInfo,
      profilePreferences,
    },
    editedProfileData: { values },
  } = useBusinessProfileReduxState()

  const { locationDisplayPreference: originalLocationDisplayPreference } =
    profilePreferences || {}

  const {
    businessEmail: originalBusinessEmail,
    businessPhoneNumber: originalBusinessPhoneNumber,
    businessWebsiteUrl: originalBusinessWebsiteUrl,
  } = (contactInfo as FormattedEditedProfileData['contactInfo']) || {}

  const {
    businessEmail: updatedBusinessEmail,
    businessPhoneNumber: updatedBusinessPhoneNumber,
    businessWebsiteUrl: updatedBusinessWebsiteUrl,
  } = (values?.contactInfo as FormattedEditedProfileData['contactInfo']) || {}
  const { locationDisplayPreference: updatedLocationDisplayPreference } =
    values?.profilePreferences || {}

  const [shouldShowExitConfirmation, setShouldShowExitConfirmation] =
    useState(false)
  const [isContactInfoValid, setIsContactInfoValid] = useState(null)

  const checkIfChangesExist = () => {
    return (
      originalLocationDisplayPreference !== updatedLocationDisplayPreference ||
      originalBusinessEmail !== updatedBusinessEmail ||
      originalBusinessPhoneNumber !== updatedBusinessPhoneNumber ||
      originalBusinessWebsiteUrl !== updatedBusinessWebsiteUrl
    )
  }

  const handleBackClick = () => {
    const changesExist = checkIfChangesExist()

    if (changesExist) {
      setShouldShowExitConfirmation(true)
    } else {
      goBackToMainPage()
    }
  }

  // EFFECTS START
  useErrorResetting(isContactInfoValid)

  useEffect(() => {
    const emptyEditedContactInfoFields =
      values?.contactInfo &&
      Object.values(values?.contactInfo).every((value) => value === '')

    if (
      businessName &&
      !isEditedDataInitialized.current &&
      emptyEditedContactInfoFields
    ) {
      // Mark as initialized
      isEditedDataInitialized.current = true

      // initialize starting contact info
      const contactInfoUpdate = {} as FormattedEditedProfileData['contactInfo']
      const newProfilePreferences =
        {} as FormattedEditedProfileData['profilePreferences']

      contactInfoUpdate.businessEmail = originalBusinessEmail
      contactInfoUpdate.businessPhoneNumber = originalBusinessPhoneNumber
      contactInfoUpdate.businessWebsiteUrl = originalBusinessWebsiteUrl

      newProfilePreferences.locationDisplayPreference =
        originalLocationDisplayPreference as LocationDisplayPreference

      dispatch(
        updateEditedProfileDataValues({
          contactInfo: contactInfoUpdate,
          profilePreferences: newProfilePreferences,
        }),
      )
    }
  }, [
    businessName,
    dispatch,
    originalBusinessEmail,
    originalBusinessPhoneNumber,
    originalBusinessWebsiteUrl,
    originalLocationDisplayPreference,
    values?.contactInfo,
  ])
  // EFFECTS END

  const contactInfoContent = (
    <div className={styles.editBizInfoContentContainer}>
      <Row noGutters className={styles.sectionContainer}>
        <BusinessNameSection />
      </Row>
      <Row noGutters className={styles.sectionContainer}>
        <EditBusinessLocationNameSection />
      </Row>
      <Row noGutters className={styles.sectionContainer}>
        <ContactInfoSection
          isValid={isContactInfoValid}
          setIsValid={setIsContactInfoValid}
          updateSelectedContactInfo={(updatedContactInfo: object) => {
            dispatch(
              updateEditedProfileDataValues({
                contactInfo: updatedContactInfo,
              }),
            )
            dispatch(updateAreValuesEdited(true))
          }}
          allContactInfo={allContactInfo}
          selectedContactInfoValues={values?.contactInfo}
        />
      </Row>
      <Row noGutters align='center' className={styles.ctaButtonContainer}>
        <PreviewProfileButton
          canPreview={isContactInfoValid}
          fpti={EDIT_BIZ_INFO_PREVIEW_BUTTON_CLICK_FPTI}
        />
        <SaveEditsButton
          canSave={isContactInfoValid}
          successfulUpdateToastMessage={bizInfoUpdatedToastMessage}
          saveButtonFpti={EDIT_BIZ_INFO_SAVE_BUTTON_CLICK_FPTI}
          successPublishFpti={EDIT_BIZ_INFO_SUCCESSFUL_SAVE_FPTI}
          onSuccess={goBackToMainPage}
        />
      </Row>
      <Row noGutters align='center'>
        <Link
          href={routes.EDIT_BUSINESS_PROFILE_SETTINGS}
          target='_blank'
          data-fpti={MODULAR_EDIT_PROFILE_PANEL_EDIT_BUSINESS_INFO_FPTI}
        >
          {getContent('editBusinessInfoLink')}
        </Link>
      </Row>
    </div>
  )

  return (
    <div
      className={styles.editBizInfoContainer}
      data-testid={EDIT_BIZ_INFO_PAGE_COMPONENT_TEST_ID}
    >
      <PageHeaderSection
        handleBackClick={handleBackClick}
        pageHeaderText={getContent('editBusinessInfoHeader')}
        backLinkFpti={EDIT_BIZ_INFO_BACK_LINK_CLICK_FPTI}
      />
      <div className={styles.desktopContainer}>
        <Card>
          <Card.Content>{contactInfoContent}</Card.Content>
        </Card>
      </div>
      <div className={styles.mobileContainer}>{contactInfoContent}</div>
      <ExitConfirmationBox
        isOpen={shouldShowExitConfirmation}
        closeConfirmationDialog={() => {
          setShouldShowExitConfirmation(false)
        }}
        handleBack={goBackToMainPage}
        fpti={{
          source: 'biz_info_page',
          ...commonModularProfileFptiProperties,
        }}
      />
    </div>
  )
}
